import { useState } from "react";
import {
  ActionIcon,
  Card,
  Group,
  Loader,
  SegmentedControl,
  Text,
  Box,
} from "@mantine/core";
import { Trash } from "tabler-icons-react";

import { config } from "config";
import {
  DIAPORAMA_MESSAGES_DELETE_AUTHORISED_ORGANIZATIONS,
  DIAPORAMA_MESSAGES_DELETE_AUTHORISED_ROLES,
} from "_roles";
import { AccessRights } from "common/admin/auth/AccessRights";
import { useOpenRemoveModal } from "common/components/useOpenRemoveModal";
import {
  useUpdateMessageMutation,
  useDeleteMessageMutation,
} from "wall/api/messages.endpoints";
import type { Message } from "wall/types/message.model";
import MessageImage from "./MessageImage";

function MessageCard({
  messageStart,
  message,
  onClick,
}: {
  messageStart: number;
  message: Message;
  onClick: VoidFunction;
}) {
  console.log("Rendering MessageCard", message);
  const [updateMessage, { isLoading }] = useUpdateMessageMutation();
  const [approved, setApproved] = useState<boolean | undefined>(
    message.approved,
  );

  const update = async (approved: boolean) => {
    setApproved(approved);
    await updateMessage({ ...message, approved });
  };

  const [deleteMsg] = useDeleteMessageMutation();

  const openRemoveModal = useOpenRemoveModal(
    `Supprimer le message ${messageStart} ?`,
    () => deleteMsg(message).then(() => onClick()),
    ".",
  );

  // Determine border color based on approval status
  const getBorderColor = () => {
    if (approved === true) return "green";
    if (approved === false) return "red";
    return undefined; // Default border color
  };
  console.log("MessageCard approved state:", approved);
  return (
    <div
      style={{
        borderColor: getBorderColor(),
        borderWidth: getBorderColor() ? 5 : 0,
        borderRadius: 8,
      }}
    >
      <Card shadow="sm" p="lg" radius="md" withBorder className="h-full">
        {/* Image section at the top */}
        <Card.Section mb="md">
          <MessageImage imageKey={message.imageKey} />
        </Card.Section>

        <Card.Section inheritPadding py="xs">
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              position: "relative",
            }}
          >
            {/* Centered segment control */}
            {isLoading ? (
              <Loader size="sm" />
            ) : (
              <SegmentedControl
                size="xs"
                value={approved ? "yes" : approved === false ? "no" : ""}
                data={[
                  { label: "YES", value: "yes" },
                  { label: "NO", value: "no" },
                ]}
                styles={(theme) => ({
                  labelActive: {
                    "&[for$=-no]": {
                      color: theme.colors.red[8],
                    },
                    "&[for$=-yes]": {
                      color: theme.colors.green[8],
                    },
                  },
                })}
                onChange={(value) => update(value === "yes")}
              />
            )}
            {/* Trash icon on the right - positioned absolutely */}
            <AccessRights
              authorizedOrganizations={
                DIAPORAMA_MESSAGES_DELETE_AUTHORISED_ORGANIZATIONS
              }
              authorizedRoles={DIAPORAMA_MESSAGES_DELETE_AUTHORISED_ROLES}
            >
              <ActionIcon
                onClick={openRemoveModal}
                color="red"
                size="sm"
                style={{ position: "absolute", right: 0 }}
              >
                <Trash size={16} />
              </ActionIcon>
            </AccessRights>
          </div>
        </Card.Section>

        {/* Content below the controls */}
        <Box mt="md">
          <Group position="apart" mb="xs">
            <Text size="sm" weight={600}>
              {message.author}
            </Text>
          </Group>

          <Box>
            <Text size="sm" style={{ lineHeight: 1.4 }}>
              {message.message.substring(0, config.walls.maxMessageSize)}
              {message.message.length > config.walls.maxMessageSize && "..."}
            </Text>
          </Box>
        </Box>
      </Card>
    </div>
  );
}

export default MessageCard;
